import { cva, type VariantProps } from 'class-variance-authority';

import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';

/**
 * Keybind keycap — the Figma "Shortcut" component (Keybind=Yes variants,
 * light 301:2674 / dark 3913:31435), shared by every DS surface that shows a
 * keyboard shortcut: `Button` (`keybinding` prop), `SegmentedControl`
 * (per-segment `keybinding`), and `Tooltip` / `Popover` ("Keyboard
 * shortcut=Yes", e.g. node 2205:12868).
 *
 * INTERNAL machinery — callers never render this directly; they pass the
 * `keybinding` prop of the host component. Web-only by DS convention: no
 * hardware-keyboard assumption on touch, and the Chivo Mono face is only
 * loaded by flashka-web. Hosts gate rendering (Button's `IS_WEB`, the
 * SegmentedControl `withKeybind` flag, Tooltip/Popover's web-only
 * `OverlayBody`).
 *
 * Anatomy: NO shadow of its own — the raised-keycap look is purely the
 * asymmetric border (thicker bottom: 1.4/1.8px vs 0.6/0.8px top+sides)
 * plus a bottom padding that lifts the glyph. Width hugs content with a
 * square `min-w == h` floor, so a single letter renders as a square key.
 *
 * State-invariant: the chip is byte-identical across default / hover /
 * pressed / focused in Figma; disabled washes it via the host-level
 * `!opacity-30`. So no state classes here.
 *
 * Colors: primary / success / alert chips are MODE-INVARIANT in Figma
 * (same hex in dark mode) → frozen inline hex, matching Button's
 * frozen-fill strategy. Secondary / tertiary / ghost chips use the
 * auto-inverting secondary tokens (light #f5f5f5/#a1a1a1/#262626 →
 * dark #171717/#525252/#e5e5e5), exactly per the dark Figma frame.
 * Note: success's border is green-500, not the -400 step the other
 * palettes use — faithful to Figma, not a typo.
 */
export const keybindChipVariants = cva(
  'flex flex-row items-center justify-center overflow-hidden border-solid shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-stable-primary-100 border-stable-primary-400',
        secondary: 'bg-secondary-100 border-secondary-400',
        tertiary: 'bg-secondary-100 border-secondary-400',
        ghost: 'bg-secondary-100 border-secondary-400',
        success: 'bg-stable-green-100 border-stable-green-500',
        alert: 'bg-stable-red-100 border-stable-red-400',
      },
      // Two chip tiers, not seven: Figma reuses "Small" and "Default" across
      // all host sizes (Button maps xxs/xs/s → sm, m–xxl → md; the Tooltip/
      // Popover chip is always sm, node 2205:12875).
      chipSize: {
        sm: 'h-4 min-w-4 rounded-[4.8px] border-[0.6px] border-b-[1.4px] px-1 pb-[1.2px]',
        md: 'h-5 min-w-5 rounded-[6px] border-[0.8px] border-b-[1.8px] px-1 pb-[1.5px]',
      },
    },
    defaultVariants: { variant: 'primary', chipSize: 'md' },
  },
);

/**
 * Keycap glyph — Chivo Mono Medium, uppercase, per Figma "Shortcut".
 * `font-chivo-mono` resolves via the shared preset; the face itself is
 * only loaded on web (flashka-web index.css) — see the web-only note above.
 */
export const keybindChipTextVariants = cva(
  'whitespace-nowrap text-center font-chivo-mono font-medium uppercase leading-[normal]',
  {
    variants: {
      variant: {
        primary: 'text-stable-primary-600',
        secondary: 'text-secondary-800',
        tertiary: 'text-secondary-800',
        ghost: 'text-secondary-800',
        success: 'text-stable-green-600',
        alert: 'text-stable-red-600',
      },
      chipSize: {
        sm: 'text-[10px] tracking-[-0.2px]',
        md: 'text-[14px] tracking-[-0.42px]',
      },
    },
    defaultVariants: { variant: 'primary', chipSize: 'md' },
  },
);

export type KeybindChipProps = VariantProps<typeof keybindChipVariants> & {
  /** Extra classes on the chip box (spacing overrides at the call site). */
  className?: string;
  /** The key glyph(s), e.g. "K", "⌘,", "esc". Rendered uppercase. */
  children: string | number;
  /** E2E id on the keycap (web `data-testid`; native never renders a keycap). */
  testID?: string;
};

export function KeybindChip({
  variant,
  chipSize,
  className,
  children,
  testID,
}: KeybindChipProps) {
  return (
    <View
      testID={testID}
      className={cn(keybindChipVariants({ variant, chipSize }), className)}>
      <Text className={keybindChipTextVariants({ variant, chipSize })}>{children}</Text>
    </View>
  );
}
